import { Clock, DollarSign, Smile, TrendingUp, X, Check, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface BenefitCard {
  id: string;
  icon: typeof Clock;
  title: string;
  description: string;
}
const BENEFITS: BenefitCard[] = [{
  id: "ahorro-tiempo",
  icon: Clock,
  title: "Ahorro de tiempo",
  description: "su equipo deja las tareas repetidas y se dedica a lo que genera valor"
}, {
  id: "ahorro-dinero",
  icon: DollarSign,
  title: "Ahorro de dinero",
  description: "menos horas perdidas y menos clientes que se van"
}, {
  id: "mejor-servicio",
  icon: Smile,
  title: "Mejor servicio",
  description: "respuestas rápidas, claras y a cualquier hora"
}, {
  id: "ventas-recuperadas",
  icon: TrendingUp,
  title: "Más ventas recuperadas",
  description: "cada mensaje y llamada se atiende, ninguno se pierde"
}];
const COMPARISON_ROWS = [{
  today: "El cliente espera horas",
  withAi: "Respuesta en segundos"
}, {
  today: "Se pierden mensajes fuera de horario",
  withAi: "Atención 24/7"
}, {
  today: "Su equipo repite las mismas respuestas",
  withAi: "La IA responde lo repetido"
}, {
  today: "Cobra tarde y a mano",
  withAi: "Recordatorios de pago automáticos"
}, {
  today: "No sabe qué se dice a sus clientes",
  withAi: "Cada conversación analizada"
}];
export default function ResultadosAntesDespues() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return <section id="resultados" data-section-id="resultados" className="bg-accent/30 text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div ref={ref} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {/* Header */}
          <div className="max-w-3xl mb-12 md:mb-16">
            <Badge variant="secondary" className="mb-4 uppercase tracking-wider font-semibold text-xs">
              RESULTADOS
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold font-default text-foreground mb-4 tracking-tight">
              Lo que su empresa ganará
            </h2>
            <p className="text-base md:text-lg text-muted-foreground font-default leading-relaxed">
              Menos esperas, menos tareas repetidas y más clientes satisfechos.
            </p>
          </div>

          {/* 4 Benefit Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {BENEFITS.map(b => {
            const Icon = b.icon;
            return <div key={b.id} className="bg-card text-card-foreground rounded-md border border-border p-6 shadow-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-xl hover:border-primary flex flex-col">
                  <div className="size-12 rounded-full bg-primary flex items-center justify-center text-primary-foreground mb-5 shrink-0">
                    <Icon className="size-6 text-white" strokeWidth={1.75} />
                  </div>
                  <h3 className="text-lg font-semibold font-default text-foreground mb-2">
                    {b.title}
                  </h3>
                  <p className="text-sm text-muted-foreground font-default leading-relaxed">
                    {b.description}
                  </p>
                </div>;
          })}
          </div>

          {/* Comparison Block: Hoy vs Con IA en sus canales */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
            {/* Column Hoy */}
            <div className="bg-card/70 rounded-md border border-border p-6 md:p-8 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-border">
                  <div className="size-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground shrink-0">
                    <X className="size-4" strokeWidth={2.5} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold font-default text-foreground">
                      Hoy
                    </h3>
                    <p className="text-xs text-muted-foreground font-default">
                      Atención tradicional y manual
                    </p>
                  </div>
                </div>

                <ul className="space-y-4">
                  {COMPARISON_ROWS.map((row, idx) => <li key={`hoy-${idx}`} className="flex items-start gap-3">
                      <div className="size-5 rounded-full bg-destructive/10 text-destructive flex items-center justify-center shrink-0 mt-0.5">
                        <X className="size-3.5" strokeWidth={2.5} />
                      </div>
                      <span className="text-sm md:text-base text-muted-foreground font-default leading-snug">
                        {row.today}
                      </span>
                    </li>)}
                </ul>
              </div>
            </div>

            {/* Column Con IA */}
            <div className="bg-card rounded-md border-2 border-primary p-6 md:p-8 shadow-md flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-border">
                  <div className="flex items-center gap-3">
                    <div className="size-8 rounded-full bg-primary flex items-center justify-center text-white shrink-0">
                      <Check className="size-4" strokeWidth={2.5} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold font-default text-foreground">
                        Con IA en sus canales
                      </h3>
                      <p className="text-xs text-secondary font-medium font-default">
                        Automatización inteligente con AVTECSA
                      </p>
                    </div>
                  </div>
                  <Badge variant="default" className="text-xs">
                    Recomendado
                  </Badge>
                </div>

                <ul className="space-y-4">
                  {COMPARISON_ROWS.map((row, idx) => <li key={`con-ia-${idx}`} className="flex items-start gap-3">
                      <div className="size-5 rounded-full bg-chart-3/15 text-chart-3 flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="size-3.5" strokeWidth={2.5} />
                      </div>
                      <span className="text-sm md:text-base font-semibold text-foreground font-default leading-snug">
                        {row.withAi}
                      </span>
                    </li>)}
                </ul>
              </div>

              <div className="pt-6 mt-6 border-t border-border">
                <Button asChild className="w-full bg-chart-4 hover:bg-chart-4/90 text-black font-bold rounded-md py-5 text-sm md:text-base shadow-sm">
                  <Link to="#contacto">
                    Descubra dónde pierde dinero
                    <ArrowRight className="ml-2 size-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}